<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { usePermissionsStore } from "@/store/permissions.store";
import type { PropType } from "vue";
import type { ITreeNode } from "../interfaces";
import { IconChevronDownRegular } from "@packages/icon-library";
import { useRoute, useRouter } from "vue-router";
import { IUser } from "@/types";

const props = defineProps({
  item: {
    type: Object as PropType<ITreeNode>,
    required: true,
  },

  user: {
    type: Object as PropType<IUser>,
    required: true,
  },
});

const emit = defineEmits(["newPath"]);

const route = useRoute();
const router = useRouter();
const permissionsStore = usePermissionsStore();

const { item } = toRefs(props);

const isActiveTab = computed(() => {
  if (typeof route.name === "string") {
    return props.item?.children?.includes(route.name);
  }
  return false;
});

const isOpen = ref<boolean>(route.path.includes(item?.value.slug ?? ""));
const isDote = computed(() => !props.item?.icon && isActiveTab.value);

function isItemVisible(item: ITreeNode): boolean {
  if (item.permission) {
    return permissionsStore.can(props.user, item.permission);
  }

  if (item.permissionGroup) {
    return item.permissionGroup.some((perm) =>
      permissionsStore.can(props.user, perm.trim())
    );
  }

  if (item.items) {
    return item.items.some((child) => isItemVisible(child));
  }

  return true;
}

const isVisible = computed(() => isItemVisible(props.item));

const clickItem = (item: ITreeNode): void => {
  if (item.type === "sub_menu") {
    isOpen.value = !isOpen.value;
  }

  if (item?.path) {
    router.push(item.path);
    emit("newPath", item.path);
  }
};
</script>

<template>
  <div v-if="isVisible">
    <button
      type="button"
      class="side-bar-menu-item"
      :class="{ 'side-bar-menu-item_active': isActiveTab }"
      @click="clickItem(item)"
    >
      <span class="side-bar-menu-item__title">
        <span
          class="side-bar-menu-item__ico"
          :class="{
            'side-bar-menu-item__ico_dote': isDote,
          }"
        >
          <Component :is="item?.icon" />
        </span>
        {{ item.title }}
      </span>
      <span
        v-if="item.items"
        class="side-bar-menu-item__chevron"
        :style="{ transform: isOpen ? 'rotate(-180deg)' : 'rotate(0)' }"
      >
        <IconChevronDownRegular />
      </span>
    </button>

    <Transition name="side-bar-menu">
      <div v-if="isOpen" class="side-bar-menu-item__node">
        <SidebarMenuItem
          v-for="(child, index) in item.items"
          :key="index"
          :item="child"
          :user="user"
          @newPath="emit('newPath', $event)"
        />
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.side-bar-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px;
  cursor: pointer;
  border-radius: 12px;
  transition: 0.1s ease-out;

  &_active {
    background: var(--color-semantic-background-normal-tertiary);

    .side-bar-menu-item__title {
      color: var(--color-semantic-content-normal-primary);

      @include control-S;
    }

    .side-bar-menu-item__chevron,
    .side-bar-menu-item__ico {
      span {
        background: var(--color-semantic-content-normal-primary);
      }
    }
  }

  &__title {
    display: flex;
    gap: 12px;
    align-items: center;
    color: var(--color-semantic-content-normal-secondary);

    @include body-S;
  }

  &__chevron,
  &__ico {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    transition: transform 0.15s;

    span {
      width: 20px;
      height: 20px;
      background: var(--color-semantic-content-normal-secondary);
    }

    &_dote {
      display: flex;
      align-items: center;
      justify-content: center;

      &::before {
        width: 6px;
        height: 6px;
        content: "";
        background: var(--color-semantic-content-normal-primary);
        border-radius: 50%;
      }
    }
  }

  &__node {
    overflow: hidden;
  }
}

.side-bar-menu-item__node {
  overflow: hidden;

  &.side-bar-menu-enter-from,
  &.side-bar-menu-leave-to {
    max-height: 0;
    opacity: 0;
  }

  &.side-bar-menu-enter-active,
  &.side-bar-menu-leave-active {
    transition:
      max-height 0.3s ease,
      opacity 0.3s ease;
  }

  &.side-bar-menu-enter-to,
  &.side-bar-menu-leave-from {
    max-height: 500px;
    opacity: 1;
  }
}

@media (hover: hover) {
  .side-bar-menu-item {
    &:hover {
      .side-bar-menu-item__title {
        color: var(--color-semantic-content-normal-primary);
      }

      .side-bar-menu-item__chevron,
      .side-bar-menu-item__ico {
        span {
          background: var(--color-semantic-content-normal-primary);
        }
      }
    }
  }
}
</style>
